import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Table, Input, notification, Skeleton, Tag, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import {
    getItemBlockMenus,
    getMenuStoresForItemBlock,
    markMenuItemBlock,
    unblockMenuItem,
} from '../../services/itemblockservice';
import {
    ItemBlockMenuItem,
    ItemBlockStoreStatus,
} from '../../types/itemblocktypes';
import { useGlobalLoading } from '../common/globalloadingcontext';
import FormButton from '../common/formbutton';
import BlockStoreSelectorDrawer from './storeselectorblockdrawer';

const { Search } = Input;

interface Props {
    userGuid: string;
    activeTab: string;
}

const ItemBlockMenusTab: React.FC<Props> = ({ userGuid, activeTab }) => {
    const { setGlobalLoading } = useGlobalLoading();
    const [data, setData] = useState<ItemBlockMenuItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Drawer state
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [selectedMenu, setSelectedMenu] = useState<ItemBlockMenuItem | null>(null);
    const [storesData, setStoresData] = useState<ItemBlockStoreStatus[]>([]);
    const [currentAction, setCurrentAction] = useState<'block' | 'unblock' | null>(null);

    const fetchMenus = async (pageNumber = 1, size = pageSize, term = search) => {
        setLoading(true);
        try {
            const res = await getItemBlockMenus(pageNumber, size, term);
            setData(res.data.items);
            setTotal(res.data.totalCount);
            setCurrentPage(pageNumber);
        } catch {
            notification.error({ message: 'Error', description: 'Failed to load menus.' });
        } finally {
            setLoading(false);
        }
    };

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setSearch(value);
            fetchMenus(1, pageSize, value);
        }, 400),
        [pageSize] // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(() => {
        if (activeTab === 'menus') {
            fetchMenus(currentPage, pageSize, search);
        }
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchMenus(currentPage, pageSize, search);
    }, [pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleManageStores = async (menu: ItemBlockMenuItem, action: 'block' | 'unblock') => {
        setDrawerLoading(true);
        setDrawerVisible(true);
        setSelectedMenu(menu);
        setCurrentAction(action);
        try {
            const res = await getMenuStoresForItemBlock(
                menu.menuGuid,
                undefined,
                action === 'block' ? 'Available' : 'Blocked'
            );
            setStoresData(res.data);
        } catch {
            notification.error({ message: 'Error', description: 'Failed to load stores.' });
        } finally {
            setDrawerLoading(false);
        }
    };

    const handleConfirmStores = async (selectedStoreGuids: string[], action: 'block' | 'unblock') => {
        if (!selectedMenu) return;
        setGlobalLoading(true);
        try {
            const request = {
                menuGuid: selectedMenu.menuGuid,
                storeGuids: selectedStoreGuids,
                userGuid,
                reason: action === 'block' ? 'Blocked via UI' : 'Unblocked via UI',
            };
            if (action === 'block') await markMenuItemBlock(request);
            else await unblockMenuItem(request);

            await fetchMenus(currentPage, pageSize, search);
            notification.success({
                message: 'Success',
                description: `${selectedMenu.name} updated for ${selectedStoreGuids.length} stores (action: ${action}).`,
            });
        } catch {
            notification.error({ message: 'Error', description: `Failed to update ${selectedMenu.name}.` });
        } finally {
            setGlobalLoading(false);
            setDrawerVisible(false);
            setSelectedMenu(null);
        }
    };

    const columns: ColumnsType<ItemBlockMenuItem> = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        {
            title: 'Stores',
            key: 'stores',
            render: (_: unknown, record: ItemBlockMenuItem) => (
                <Space style={{ display: 'flex', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 120 }}>Total Stores: {record.totalStores}</div>
                    <div style={{ flex: 1, minWidth: 160 }}>
                        <Tag color="red">Blocked in {record.blockedCount} stores</Tag>
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                        <Tag color="green">Available in {record.availableCount} stores</Tag>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'manage',
            render: (_: unknown, record: ItemBlockMenuItem) => (
                <Space>
                    <FormButton title="Block" onClick={() => handleManageStores(record, 'block')} />
                    <FormButton title="Unblock" onClick={() => handleManageStores(record, 'unblock')} />
                </Space>
            ),
        },
    ];

    return (
        <>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
                <Col>
                    <Search
                        placeholder="Search menus"
                        onChange={e => debouncedSearch(e.target.value)}
                        style={{ width: 350 }}
                        allowClear
                    />
                </Col>
            </Row>

            {loading && data.length === 0 ? (
                <Skeleton active paragraph={{ rows: 5 }} />
            ) : (
                <Table<ItemBlockMenuItem>
                    dataSource={data}
                    columns={columns}
                    loading={loading && data.length > 0}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        showTotal: (t, range) => `Showing ${range[0]}-${range[1]} of ${t} items`,
                        onChange: (page, size) => {
                            setPageSize(size!);
                            fetchMenus(page, size!, search);
                        },
                    }}
                    rowKey="menuGuid"
                />
            )}

            {selectedMenu && currentAction && (
                <BlockStoreSelectorDrawer
                    visible={drawerVisible}
                    onClose={() => setDrawerVisible(false)}
                    onOk={handleConfirmStores}
                    itemName={selectedMenu.name}
                    stores={storesData}
                    loading={drawerLoading}
                    action={currentAction}
                />
            )}
        </>
    );
};

export default ItemBlockMenusTab;